import {
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  isWeekend,
} from 'date-fns';
import { ReactComponent as StatusIcon } from '../assets/icons/status-icon.svg';
import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { ReactComponent as ReportIcon } from '../assets/icons/insert_drive_file.svg';
import {
  ABSENCE_REQUEST_FIELD_NAMES,
  ACCRUAL_OPTIONS,
  ABSENCE_CALENDAR_INTERVALS,
  ABSENCE_CALENDAR_INTERVAL_TYPES,
  ABSENCE_ICONS,
  ABSENCE_FIELD_NAMES,
} from '../constants/absence';
import { api_user_absence_available_days } from '../constants/apiRoutes';
import { SINGLE_DAY_SAVE_FORMAT } from '../constants/date';
import {
  ABSENCE_REQUEST_STATUSES,
  ABSENCE_REQUEST_STATUS_TYPES,
} from '../constants/statuses';
import { PARAMS } from '../constants/pages';
import { ABSENCE_FEATURE_INITIAL_DATE } from '../constants/appConfig';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { isEmpty, forbiddenCharacters, validateLength } from './validation';
import {
  formatDate,
  checkIsDateAfterPrevious,
  checkIsSameDay,
  isSameDate,
  isSameMonthCheck,
  checkIsSameYear,
  getDifferenceInCalendarYears,
  getStartOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  getEndOfWeek,
  checkIfDateIsInRange,
  getDayOfTheMonth,
  getMonthFromDate,
  getYearFromDate,
} from './dateUtils';
import http from './http';
import { getItemById, isArrayEmpty } from './helpers';
import { parseDuplicateParameters } from './uiUtils';

const {
  ICON,
  IS_PREVIOUS_YEAR_MANUAL_BALANCE,
  HAS_REASONS,
  REASONS,
  USER_COUNT,
} = ABSENCE_FIELD_NAMES;
const {
  AMOUNT,
  ABSENCE,
  ABSENCE_FROM,
  ABSENCE_TO,
  DESCRIPTION,
  USED_DAYS,
  REASON,
  USER,
} = ABSENCE_REQUEST_FIELD_NAMES;

export const prepareRolloverDate = rolloverDate => {
  const day = getDayOfTheMonth(rolloverDate);
  const month = getMonthFromDate(rolloverDate);
  const year = getYearFromDate(new Date());

  return formatDate(new Date(year + 1, month, day), SINGLE_DAY_SAVE_FORMAT);
};

const prepareRequestReson = (reasons, reason) =>
  reasons.some(r => {
    if (r.id) {
      return r?.name?.toLowerCase() === reason?.toLowerCase();
    }

    return r?.toLowerCase() === reason?.toLowerCase();
  })
    ? reason
    : '';

export const prepareReasons = reasons =>
  reasons.reduce(
    (acc, currentReason, index) => [
      ...acc,
      {
        id: index + 1,
        name: currentReason,
      },
    ],
    []
  );

export const prepareTableRequestDescription = (
  absence,
  description,
  reason
) => {
  if (absence[HAS_REASONS]) {
    const currentReason = prepareRequestReson(absence[REASONS], reason);

    return currentReason || '-';
  }

  return description || '-';
};

export const prepareYearOptions = () => {
  const currentYear = +formatDate(new Date(), 'yyyy');

  return Array.from(
    {
      length:
        getDifferenceInCalendarYears(new Date(), ABSENCE_FEATURE_INITIAL_DATE) +
        2,
    },
    (_, i) => ({
      label: currentYear - i + 1,
      value: currentYear - i + 1,
    })
  );
};

export const prepareUserAbsences = (absences = []) => {
  return absences.reduce((acc, absence) => {
    const absenceIcon = getItemById(ABSENCE_ICONS, absence[ICON])?.icon;

    if (!absence[IS_PREVIOUS_YEAR_MANUAL_BALANCE]) {
      return [
        ...acc,
        {
          ...absence,
          id: absence.absence,
          absenceIcon,
          [REASONS]: prepareReasons(absence[REASONS]),
        },
      ];
    }
    return acc;
  }, []);
};

export const prepareUserAvailableAbsenceTypes = userAbsenceTypes =>
  userAbsenceTypes.filter(aType => !aType.is_manual_total);

export const prepareAccrualOptions = labels =>
  ACCRUAL_OPTIONS.map(accrualOption => ({
    ...accrualOption,
    name: labels[accrualOption.key],
  }));

export const prepareTranslatedAbsenceRequestStatuses = (
  labels,
  isFilter = false
) =>
  ABSENCE_REQUEST_STATUSES.map(reqStatus => ({
    ...reqStatus,
    ...(isFilter
      ? { name: labels[reqStatus.key] }
      : {
          name:
            reqStatus.key === 'halfApproved'
              ? labels.requested
              : labels[reqStatus.key],
        }),
  }));

export const prepareAbsenceSetsData = (translations, sets, accrualOptions) => {
  return sets.map(set => ({
    ...set,
    types: set?.types.map(absence => {
      const iconOption = getItemById(ABSENCE_ICONS, absence[ICON]);
      const currentAccrual = accrualOptions.find(
        accrual => accrual.value === absence.accrual
      );

      return {
        ...absence,
        absenceIcon: iconOption?.icon,
        ...(currentAccrual
          ? {
              accrualLabel: `${
                translations.accrual
              } ${currentAccrual.name.toLowerCase()}`,
            }
          : {}),
      };
    }),
  }));
};

export const prepareManageAbsenceRequestInitialData = (
  absenceRequest,
  user = null
) => {
  if (absenceRequest) {
    return {
      ...absenceRequest,
      [ABSENCE]: absenceRequest[ABSENCE] || null,
      [AMOUNT]: Math.abs(absenceRequest[USED_DAYS]),
      [DESCRIPTION]: !absenceRequest[ABSENCE][HAS_REASONS]
        ? absenceRequest[DESCRIPTION]
        : '',
      [REASON]: !absenceRequest[ABSENCE][HAS_REASONS]
        ? ''
        : prepareRequestReson(
            absenceRequest[ABSENCE][REASONS],
            absenceRequest[REASON]
          ),
    };
  }

  return {
    user,
    [AMOUNT]: 0,
    [ABSENCE]: null,
    [REASON]: '',
    [DESCRIPTION]: '',
    [ABSENCE_FROM]: null,
    [ABSENCE_TO]: null,
  };
};

export const prepareWorkdays = (dateFrom, dateTo) =>
  eachDayOfInterval({ start: new Date(dateFrom), end: new Date(dateTo) })
    .filter(date => !isWeekend(date))
    .map(date => formatDate(new Date(date), 'y-MM-dd'));

export const prepareManageAbsenceRequestFields = (
  classes,
  currentYear,
  isEdit = false,
  hasUserField = false,
  getAbsences = () => {}
) => {
  return [
    ...(hasUserField
      ? [
          {
            name: USER,
            fieldWrapperClass: classes.userField,
            type: FIELD_TYPES.SELECT,
            optionsKey: 'allUsers',
            isUser: true,
            shouldReturnOption: true,
            parser: {
              value: 'id',
              label: 'first_name',
            },
            isDisabled: isEdit,
            required: true,
            dependants: [
              {
                name: ABSENCE,
                callbackFnc: (prevUser, currentUser) =>
                  getAbsences(currentUser?.id),
              },
              {
                name: ABSENCE,
                value: null,
              },
              {
                name: ABSENCE_FROM,
                value: null,
              },
              {
                name: ABSENCE_TO,
                value: null,
              },
              {
                name: REASON,
                value: '',
              },
              {
                name: DESCRIPTION,
                value: '',
              },
            ],
            validators: [
              {
                type: 'required',
                validator: isEmpty,
              },
            ],
          },
        ]
      : []),
    {
      name: ABSENCE_FROM,
      type: FIELD_TYPES.DATE_PICKER,
      translationKey: 'absenceFrom',
      fieldWrapperClass: classes.fromField,
      required: true,
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      getMinDate: () => new Date(currentYear, 0, 1),
      getMaxDate: () => new Date(currentYear + 1, 11, 31),
      shouldDisableDate: isWeekend,
      dependants: [
        {
          name: ABSENCE_TO,
          resetFnc: (prevFromValue, currentFromValue, currentToValue) =>
            checkIsSameDay(currentFromValue, currentToValue) ||
            checkIsDateAfterPrevious(currentToValue, currentFromValue)
              ? currentToValue
              : currentFromValue,
        },
        {
          name: AMOUNT,
          resetFnc: async (
            prevFromValue,
            currentFromValue,
            currentAmountValue,
            initialValues,
            values
          ) => {
            if (
              currentFromValue &&
              currentFromValue === initialValues?.[ABSENCE_FROM] &&
              values?.[ABSENCE_TO] &&
              values[ABSENCE_TO] === initialValues?.[ABSENCE_TO]
            ) {
              return initialValues?.[AMOUNT];
            }

            if (values.user) {
              const absenceTo =
                values?.[ABSENCE_TO] &&
                (checkIsSameDay(currentFromValue, values[ABSENCE_TO]) ||
                  checkIsDateAfterPrevious(
                    values[ABSENCE_TO],
                    currentFromValue
                  ))
                  ? values[ABSENCE_TO]
                  : currentFromValue;
              const absenceRequestDates = prepareWorkdays(
                currentFromValue,
                absenceTo
              );
              const params = {
                [PARAMS.ABSENCE_REQUEST_DATES]: absenceRequestDates,
                ...(values.id ? { absence_request: values.id } : {}),
              };
              const { data } = await http.get(
                api_user_absence_available_days(values[USER].id),
                { params, paramsSerializer: d => parseDuplicateParameters(d) }
              );

              return data.days;
            }

            return currentAmountValue;
          },
        },
      ],
      validators: [
        {
          type: 'required',
          validator: isEmpty,
        },
      ],
    },
    {
      name: ABSENCE_TO,
      type: FIELD_TYPES.DATE_PICKER,
      translationKey: 'absenceTo',
      fieldWrapperClass: classes.toField,
      required: true,
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      getMinDate: (fieldValue, values) =>
        values[ABSENCE_FROM] || new Date(currentYear, 0, 1),
      getMaxDate: () => new Date(currentYear + 1, 11, 31),
      shouldDisableDate: isWeekend,
      dependants: [
        {
          name: ABSENCE_FROM,
          resetFnc: (prevFromValue, currentToValue, currentFromValue) =>
            currentFromValue === null ? currentToValue : currentFromValue,
        },
        {
          name: AMOUNT,
          resetFnc: async (
            prevFromValue,
            currentToValue,
            currentAmountValue,
            initialValues,
            values
          ) => {
            if (
              currentToValue &&
              currentToValue === initialValues?.[ABSENCE_TO] &&
              values?.[ABSENCE_FROM] &&
              values[ABSENCE_FROM] === initialValues?.[ABSENCE_FROM]
            ) {
              return initialValues?.[AMOUNT];
            }

            if (values[USER]) {
              const absenceFrom = values?.[ABSENCE_FROM]
                ? values[ABSENCE_FROM]
                : currentToValue;
              const absenceRequestDates = prepareWorkdays(
                absenceFrom,
                currentToValue
              );
              const params = {
                [PARAMS.ABSENCE_REQUEST_DATES]: absenceRequestDates,
                ...(values.id ? { absence_request: values.id } : {}),
              };

              const { data } = await http.get(
                api_user_absence_available_days(values[USER].id),
                { params, paramsSerializer: d => parseDuplicateParameters(d) }
              );

              return data.days;
            }

            return currentAmountValue;
          },
        },
      ],
      validators: [
        {
          type: 'required',
          validator: isEmpty,
        },
      ],
    },
    {
      name: AMOUNT,
      type: FIELD_TYPES.TEXT,
      isDisabled: true,
      inputType: 'number',
      required: true,
      shouldValidateDisabledField: true,
      validators: [
        {
          type: 'minDays',
          validator: value => value !== 0,
        },
      ],
    },
    {
      name: ABSENCE,
      type: FIELD_TYPES.SELECT,
      fieldWrapperClass: classes.absenceField,
      required: true,
      optionsKey: 'absences',
      shouldReturnOption: true,
      parser: {
        value: 'id',
        label: 'name',
      },
      dependants: [
        {
          name: DESCRIPTION,
          resetFnc: (
            prevAbsence,
            currentAbsence,
            currentDescription,
            initialValues
          ) => {
            if (currentAbsence?.id === initialValues?.[ABSENCE]?.id) {
              return initialValues[DESCRIPTION];
            }

            return '';
          },
        },
        {
          name: REASON,
          resetFnc: (
            prevAbsence,
            currentAbsence,
            currentReason,
            initialValues
          ) => {
            if (currentAbsence?.id === initialValues?.[ABSENCE]?.id) {
              return initialValues[REASON];
            }

            return '';
          },
        },
      ],
      validators: [
        {
          type: 'required',
          validator: isEmpty,
        },
      ],
    },
    {
      name: REASON,
      type: FIELD_TYPES.SELECT,
      fieldWrapperClass: classes.reasonField,
      optionsKey: REASONS,
      isToggleable: true,
      shouldFadeOutInstant: true,
      parent: {
        name: ABSENCE,
        options: 'absences',
      },
      parser: {
        value: 'name',
        label: 'name',
      },
      shouldRender: values =>
        values?.[ABSENCE] && values?.[ABSENCE]?.[HAS_REASONS],
      dependants: [
        {
          name: DESCRIPTION,
          value: '',
        },
      ],
    },
    {
      name: DESCRIPTION,
      type: FIELD_TYPES.TEXT,
      fieldWrapperClass: classes.descriptionField,
      multiline: true,
      rows: 5,
      isToggleable: true,
      shouldFadeOutInstant: true,
      shouldRender: values =>
        values?.[ABSENCE] && !values?.[ABSENCE]?.[HAS_REASONS],
      validators: [
        {
          type: 'forbiddenCharacters',
          validator: forbiddenCharacters,
        },
        {
          type: 'descriptionMaxLength',
          validator: fieldValue => validateLength(fieldValue, 0, 255),
        },
      ],
    },
  ];
};

export const prepareAbsenceRequestsPageFilters = (translations, statuses) => {
  const { STATUS } = PARAMS;
  const defaultOption = { id: null, name: translations.all };

  return [
    {
      id: STATUS,
      name: translations.filters.labels[STATUS],
      icon: StatusIcon,
      isSingleSelect: true,
      items: [defaultOption, ...statuses],
    },
  ];
};

export const prepareAbsenceRequestMenuItems = (
  labels,
  request,
  hasEdit,
  hasDelete,
  hasOpenReport,
  onEdit,
  onDelete,
  onOpenReport
) => {
  return [
    {
      type: 'edit',
      label: labels.edit,
      shouldItemRender: () => hasEdit,
      action: () => onEdit(request),
      icon: EditIcon,
    },
    {
      type: 'report',
      label: labels.report,
      shouldItemRender: () =>
        hasOpenReport && isEmpty(request?.pdf_report?.file),
      action: () => onOpenReport(request),
      icon: ReportIcon,
    },
    {
      type: 'delete',
      label: labels.delete,
      shouldItemRender: () => hasDelete,
      action: () => onDelete(request),
      icon: DeleteIcon,
    },
  ];
};

export const prepareUpcomingTimeOffPeriodLabel = (fromDate, toDate) => {
  const DAY_MONTH_FORMAT = 'dd MMM';

  if (isSameDate(fromDate, toDate)) {
    return formatDate(fromDate);
  }

  if (checkIsSameYear(toDate, fromDate)) {
    return `${formatDate(fromDate, DAY_MONTH_FORMAT)} - ${formatDate(toDate)}`;
  }

  return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
};

export const prepareRequestPeriodLabel = (
  translations,
  fromDate,
  toDate,
  usedDays
) => {
  const DAY_MONTH_FORMAT = 'dd MMM';

  if (isSameDate(fromDate, toDate)) {
    return `${formatDate(fromDate)} (${usedDays} ${
      usedDays === 1 ? translations.day : translations.days
    })`;
  }

  if (checkIsSameYear(toDate, fromDate)) {
    return `${formatDate(fromDate, DAY_MONTH_FORMAT)} - ${formatDate(
      toDate
    )} (${usedDays} ${usedDays === 1 ? translations.day : translations.days})`;
  }

  return `${formatDate(fromDate)} - ${formatDate(toDate)} (${usedDays} ${
    usedDays === 1 ? translations.day : translations.days
  })`;
};

export const prepareAbsenceCalendarInterval = labels =>
  ABSENCE_CALENDAR_INTERVALS.map(calendarInterval => ({
    ...calendarInterval,
    label: labels[calendarInterval.key],
  }));

export const prepareAbsenceCalendarDateRange = (interval, intervalPage) => {
  const weeksToAdd = 4;
  const today = new Date();
  const weekStart = getStartOfWeek(today);
  const monthStart = getStartOfMonth(today);

  let startDate;
  let endDate;

  if (interval === ABSENCE_CALENDAR_INTERVAL_TYPES.WEEK) {
    if (Math.sign(intervalPage) >= 0) {
      startDate = addWeeks(weekStart, intervalPage);
      endDate = addWeeks(getEndOfWeek(weekStart), weeksToAdd + intervalPage);
    } else {
      startDate = subWeeks(weekStart, Math.abs(intervalPage));
      endDate = addWeeks(getEndOfWeek(startDate), weeksToAdd);
    }
  }

  if (interval === ABSENCE_CALENDAR_INTERVAL_TYPES.MONTH) {
    if (Math.sign(intervalPage) >= 0) {
      startDate = addMonths(monthStart, intervalPage);
      endDate = getEndOfMonth(addMonths(monthStart, intervalPage));
    } else {
      startDate = subMonths(monthStart, Math.abs(intervalPage));
      endDate = getEndOfMonth(startDate);
    }
  }

  return {
    startDate,
    endDate,
  };
};

export const prepareAbsenceCalendarData = (dateRange, interval) => {
  const isMonthInterval = interval === ABSENCE_CALENDAR_INTERVAL_TYPES.MONTH;
  const today = new Date();
  const intervalDays = eachDayOfInterval({
    start: dateRange.startDate,
    end: dateRange.endDate,
  }).reduce(
    (acc, date) => [
      ...acc,
      {
        date,
        isWeekend: isWeekend(date),
        day: formatDate(date, 'd'),
        dayOfTheWeek: formatDate(date, 'EEEEEE'),
      },
    ],
    []
  );
  const weeks = eachWeekOfInterval(
    {
      start: dateRange.startDate,
      end: dateRange.endDate,
    },
    { weekStartsOn: 1 }
  );
  const monthLabel = formatDate(dateRange.startDate, 'MMM yyyy');
  const weekLabels = weeks.reduce((acc, currentWeekDate, index) => {
    const endOfWeek = getEndOfWeek(currentWeekDate);
    const label = isSameMonthCheck(endOfWeek, currentWeekDate)
      ? formatDate(currentWeekDate, 'MMM yyyy')
      : `${formatDate(
          currentWeekDate,
          checkIsSameYear(endOfWeek, currentWeekDate) ? 'MMM' : ' MMM yyyy'
        )} |
        ${formatDate(endOfWeek, 'MMM yyyy')}`;

    return [
      ...acc,
      {
        weekDay: currentWeekDate,
        label:
          index !== 0 &&
          isSameMonthCheck(currentWeekDate, endOfWeek) &&
          isSameMonthCheck(currentWeekDate, getEndOfWeek(weeks[index - 1]))
            ? null
            : label,
      },
    ];
  }, []);

  const todayPositionIndex = intervalDays.findIndex(day =>
    checkIsSameDay(day.date, today)
  );

  return {
    intervalDays,
    weekLabels,
    monthLabel,
    isMonthInterval,
    todayPositionIndex,
  };
};

export const prepareAbsenceCalendarResults = (users, calendar) => {
  return users.reduce((acc, user) => {
    const days = calendar.intervalDays.reduce((daysAcc, dayData) => {
      const holiday = user.holidays.find(hDay =>
        checkIsSameDay(hDay.date, dayData.date)
      );

      const absenceRequests =
        !dayData.isWeekend && !holiday
          ? user.absence_requests.filter(
              request =>
                request.used_days !== 0 &&
                request.status !== ABSENCE_REQUEST_STATUS_TYPES.REJECTED &&
                checkIfDateIsInRange(
                  formatDate(dayData.date, 'y-MM-dd'),
                  request.absence_from,
                  request.absence_to
                )
            )
          : [];
      const isApprovedAbsence = absenceRequests.some(
        request => request.status === ABSENCE_REQUEST_STATUS_TYPES.APPROVED
      );

      const isHalfApprovedAbsence = absenceRequests.some(
        request => request.status === ABSENCE_REQUEST_STATUS_TYPES.HALF_APPROVED
      );
      return [
        ...daysAcc,
        {
          ...dayData,
          holiday: holiday || null,
          hasAbsenceRequest: !isArrayEmpty(absenceRequests),
          absenceRequests,
          isApprovedAbsence,
          isHalfApprovedAbsence,
        },
      ];
    }, []);

    return [...acc, { user, intervalDays: days }];
  }, []);
};

export const prepareAbsenceSetOptions = (absenceSets = []) => {
  return absenceSets.reduce((acc, absenceSet) => {
    if (
      !isArrayEmpty(absenceSet?.types ?? []) &&
      (absenceSet?.types ?? []).some(type => type[USER_COUNT])
    ) {
      return [...acc, { ...absenceSet }];
    }
    return acc;
  }, []);
};

export const prepareAbsenceTypesOptions = (absenceSetTypes = []) => {
  return absenceSetTypes.reduce((acc, absenceType) => {
    if (absenceType[USER_COUNT]) {
      return [...acc, { ...absenceType }];
    }
    return acc;
  }, []);
};
